/**
 * The show style (D32, Sofie's ShowStyle): a tenant-level library of what an
 * operator plays — piece types (params, lifespan, source/output layer, default
 * timing, timeline templates), source and output layers, adlib panels and
 * macro actions — written against ABSTRACT timeline layers (`layers`) that
 * each studio maps to its own devices (`studio.ts`). One show style serves
 * many studios; a production picks a studio and a show style, and
 * `resolveProductionConfig` (`resolveConfig.ts`) turns the pair into the
 * production config the engine runs (`productionConfig.ts`).
 *
 *   layers        the abstract timeline layers the templates target: a label,
 *                 and the drivers a studio may map each to (`deviceTypes`;
 *                 empty = any — the templates' content is device-specific,
 *                 so a show style usually names one)
 *   sourceLayers / outputLayers / pieceTypes / actions / adlibPanels
 *                 as in the production config (the same schemas)
 *   keymap        the operator screen's keys per operator action (`keymap.ts`);
 *                 absent: the built-in keys. Optional, so a show style that
 *                 names none stores exactly what it did before
 *   mosIngest     RESERVED for D31's declarative MOS ingest rules (PLAN §7.6):
 *                 null, or an empty object, until that work defines it
 *
 * A stable export for the same reason as the production config: the API
 * stores what this schema outputs (defaults applied). Additive only.
 */
import { z } from 'zod';
import { keymapSchema } from './keymap.js';
import {
  actionSchema,
  adlibPanelSchema,
  configIdSchema,
  definitionProblems,
  deviceTypeSchema,
  DEVICE_TYPES,
  layerIdSchema,
  outputLayerSchema,
  pieceTypeSchema,
  sourceLayerSchema,
} from './productionConfig.js';

/** One abstract timeline layer of a show style. */
export const showStyleLayerSchema = z
  .object({
    label: z.string().max(200).default(''),
    /** The drivers a studio may map this layer to (the templates' content speaks theirs); empty = any. */
    deviceTypes: z.array(deviceTypeSchema).max(DEVICE_TYPES.length).default([]),
  })
  .strict();

/**
 * D31's MOS ingest rules, reserved: the section exists so a show style can
 * carry them without a schema break, and holds nothing until the MOS work
 * defines the rules (any key is refused until then).
 */
export const mosIngestSchema = z.object({}).strict();

const showStyleConfigObjectSchema = z
  .object({
    layers: z.record(layerIdSchema, showStyleLayerSchema).default({}),
    sourceLayers: z.record(layerIdSchema, sourceLayerSchema).default({}),
    outputLayers: z.record(layerIdSchema, outputLayerSchema).default({}),
    pieceTypes: z.record(configIdSchema, pieceTypeSchema).default({}),
    actions: z.record(configIdSchema, actionSchema).default({}),
    adlibPanels: z.array(adlibPanelSchema).default([]),
    mosIngest: mosIngestSchema.nullable().default(null),
    keymap: keymapSchema.optional(),
  })
  .strict();

/**
 * The whole show style, every cross-reference checked: a piece type's
 * source/output layer and timeline layers exist (`layers`), an action or
 * panel item names an existing piece type or action.
 */
export const showStyleConfigSchema = showStyleConfigObjectSchema.superRefine((config, ctx) => {
  for (const problem of definitionProblems(config, config.layers, 'layer')) ctx.addIssue({ code: 'custom', path: problem.path, message: problem.message });
});

export type ShowStyleConfig = z.output<typeof showStyleConfigSchema>;
/** What a caller may write: defaults not yet applied. */
export type ShowStyleConfigInput = z.input<typeof showStyleConfigSchema>;

/** The piece types whose templates target each layer, by layer id (a layer no template uses is absent). */
export function layerUsers(showStyle: Pick<ShowStyleConfig, 'pieceTypes'>): ReadonlyMap<string, readonly string[]> {
  const users = new Map<string, string[]>();
  for (const [id, type] of Object.entries(showStyle.pieceTypes)) {
    for (const entry of type.timeline) {
      const list = users.get(entry.layer) ?? [];
      if (!list.includes(id)) list.push(id);
      users.set(entry.layer, list);
    }
  }
  return users;
}

export type ParseShowStyleResult = { readonly ok: true; readonly config: ShowStyleConfig } | { readonly ok: false; readonly error: string };

/** Parse and cross-check untrusted show-style input. Never throws. */
export function parseShowStyleConfig(input: unknown): ParseShowStyleResult {
  const result = showStyleConfigSchema.safeParse(input);
  return result.success ? { ok: true, config: result.data } : { ok: false, error: z.prettifyError(result.error) };
}
